import { Country, SuperviselyDatasetEnum, useApiQualityExternalUploadV1Create } from 'api';
import { formatDateToDateTimeIsoString } from 'date';
import { useNotification } from 'features';
import {
  CountryAutocompleteField,
  DateField,
  Form,
  SelectField,
  SiteMaterialAutocomplete,
  TextField,
  TradeCompanyAutocomplete,
  useForm,
  yup,
  yupResolver,
} from 'form';
import { useTranslation } from 'i18n';
import { Box, Button, Grid } from 'ui';

const DATASET_OPTIONS = Object.values(SuperviselyDatasetEnum).map((dataset) => ({
  label: dataset,
  value: dataset,
}));

const schema = yup.object().shape({
  country: yup.string().oneOf(Object.values(Country)).optional().nullable(),
  sample_id: yup.string().optional(),
  site_description: yup.string().optional(),
  site_material_id: yup.number().optional().nullable(),
  supervisely_dataset: yup.string().oneOf(Object.values(SuperviselyDatasetEnum)).required(),
  taken_at: yup.string().required(),
  trade_company_id: yup.number().optional().nullable(),
});

type FormValues = yup.InferType<typeof schema>;

export const ExternalEvidenceForm = ({ onSubmitSuccess }: { onSubmitSuccess: (id: string) => void }) => {
  const { t } = useTranslation('orders');
  const { open } = useNotification();
  const form = useForm({
    resolver: yupResolver(schema),
  });

  const tradeCompanyId = form.watch('trade_company_id');

  const { mutate, isPending, isSuccess } = useApiQualityExternalUploadV1Create({
    mutation: {
      onError: () => {
        open({
          message: t('externalEvidencePage.form.notifications.errorCreatingExternalEvidence'),
          type: 'error',
        });
      },
      onSuccess: (data) => {
        open({
          description: t('externalEvidencePage.form.notifications.canNowUploadEvidence'),
          message: t('externalEvidencePage.form.notifications.externalEvidenceCreated'),
          type: 'success',
        });
        onSubmitSuccess(data.uuid);
      },
    },
  });
  const onSubmit = (data: FormValues) => {
    mutate({
      data: {
        ...data,
        taken_at: formatDateToDateTimeIsoString(data.taken_at),
      },
    });
  };

  return (
    <Form {...form} onSubmit={onSubmit}>
      <Grid container>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <SelectField
            label={t('externalEvidencePage.form.labels.superviselyDataset')}
            name="supervisely_dataset"
            options={DATASET_OPTIONS}
            required={true}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <DateField label={t('externalEvidencePage.form.labels.takenAt')} name="taken_at" fullWidth required={true} />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <TradeCompanyAutocomplete
            name="trade_company_id"
            label={t('externalEvidencePage.form.labels.tradeCompany')}
            required={false}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <SiteMaterialAutocomplete
            name="site_material_id"
            label={t('externalEvidencePage.form.labels.siteMaterial')}
            tradeCompanyId={tradeCompanyId ?? undefined}
            required={false}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <CountryAutocompleteField
            name="country"
            label={t('externalEvidencePage.form.labels.country')}
            required={false}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <TextField
            multiline
            label={t('externalEvidencePage.form.labels.siteDescription')}
            name="site_description"
            required={false}
          />
        </Grid>
        <Grid
          size={{
            md: 4,
            xs: 12,
          }}
        >
          <TextField label={t('externalEvidencePage.form.labels.sampleId')} name="sample_id" required={false} />
        </Grid>
        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button type="submit" loading={isPending} disabled={isSuccess}>
              {t('externalEvidencePage.form.actions.confirm')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Form>
  );
};
